import {css, StyleSheet} from 'aphrodite';
import React, {ChangeEvent, ClipboardEvent, useRef} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import Row from 'ui-library/bs/Row';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT} from 'ui-library/Colors';
import useFocusState from 'ui-library/hooks/useFocusState';
import {WorklistType} from 'worklists/WorklistConstants';

interface BarcodeScanningHeaderProps {
  value: string;
  onChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  onPaste: (e: ClipboardEvent<HTMLTextAreaElement>) => void;
  worklistType: WorklistType;
}

/**
 * Controlled component with a hidden textarea for receiving barcodes
 * and a button to indicate and reset focus state
 */
export default function BarcodeScanningHeader(props: BarcodeScanningHeaderProps): JSX.Element {
  const [isScanning, textareaFocusProps] = useFocusState(true);
  const textareaEl = useRef<HTMLTextAreaElement>(null);

  const setFocus = (): void => {
    if (textareaEl.current && !isScanning) {
      textareaEl.current.focus();
    }
  };

  const buttonText =
    props.worklistType === WorklistType.BIOENTITY
      ? 'Click to scan / paste registry IDs'
      : 'Click to scan / paste barcodes';

  return (
    <>
      <Row>
        <div className="u-flex-display u-vertical-center-items u-justify-content-center">
          <Button
            onClick={() => {
              setFocus();
            }}
            onMouseDown={(e) => {
              if (isScanning) {
                // If we're already scanning but trying to press the button, we'll be blurring away from
                // the textarea and then re-focusing on it. This results in a flicker where the user sees
                // the button flash blue -> orange -> blue. To prevent this, we can prevent the blur event
                // in the first place by catching it in the mousedown, which happens before blur.
                // See https://codepen.io/mudassir0909/pen/eIHqB for demo.
                e.preventDefault();
              }
            }}
            icon="barcode"
            className={isScanning ? css(styles.button) : undefined}
            buttonStyle={isScanning ? ButtonStyle.TERTIARY_GRAY : ButtonStyle.SECONDARY}
            size={ButtonSize.LARGE}
            text={isScanning ? 'Accepting input...' : buttonText}
          />
        </div>
      </Row>
      <Row>
        <textarea
          className={css(styles.hidden)}
          ref={textareaEl}
          value={props.value}
          onChange={props.onChange}
          onPaste={props.onPaste}
          {...textareaFocusProps}
          {...getTestProps({component: 'BarcodeScanningHeader', element: 'textarea'})}
        />
      </Row>
    </>
  );
}

const styles = StyleSheet.create({
  hidden: {
    height: 0,
    width: 0,
    border: 'none',
    padding: 0,
  },
  button: {
    // Don't show the hand or darker color on hover when isScanning is already true,
    // as clicking the button won't do anything
    cursor: 'default',
    ':hover': {
      background: GRAY_LIGHT,
    },
  },
});
